import { useEffect, useState } from "react";
import { accountApi } from "../../services/accountApi";
import { authApi } from "../../services/authApi";
import { useToast } from "../../context/ToastContext";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { DataTable } from "../../components/common/DataTable";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { Modal } from "../../components/common/Modal";

export function SecurityCenterTab() {
  const { notify } = useToast();
  const [sessions, setSessions] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null);
  const [answers, setAnswers] = useState(["", "", ""]);
  const [confirmLogoutAll, setConfirmLogoutAll] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    accountApi.sessions().then((r) => setSessions(r.sessions));
  }, []);

  async function generateBackupCodes() {
    setBusy(true);
    try {
      const r = await accountApi.generateBackupCodes();
      setBackupCodes(r.backup_codes);
    } catch (err) {
      notify(err.apiMessage || "Could not generate backup codes", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function saveSecurityQuestions() {
    const filled = answers.filter((a) => a.trim());
    if (filled.length === 0) return notify("Enter at least one answer", "warning");
    setBusy(true);
    try {
      await accountApi.setSecurityQuestions(filled);
      notify("Security questions saved", "success");
    } catch (err) {
      notify(err.apiMessage || "Could not save security questions", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function revokeSession(sessionId) {
    await accountApi.revokeSession(sessionId);
    setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
  }

  async function logoutAll() {
    setBusy(true);
    try {
      const r = await authApi.logoutAll();
      notify(`Signed out of ${r.sessions_revoked} other session(s)`, "success");
      const refreshed = await accountApi.sessions();
      setSessions(refreshed.sessions);
    } finally {
      setBusy(false);
      setConfirmLogoutAll(false);
    }
  }

  return (
    <div>
      <Card
        title="Active Sessions"
        actions={<Button size="sm" variant="danger" onClick={() => setConfirmLogoutAll(true)}>Log Out All Other Sessions</Button>}
        className="section-spacing"
      >
        <DataTable
          rows={sessions}
          loading={!sessions}
          rowKey="session_id"
          /* The generic "Nothing here yet" was actively wrong here. On a
             SECURITY screen an empty list is not a gap waiting to be
             filled -- it is the reassuring answer, and it should read
             like one. Somebody checking this page is asking "is anyone
             else in my account", and "Nothing here yet" does not answer
             that question. */
          emptyTitle="Only this device"
          emptyDescription="No other sessions are signed in to your account."
          columns={[
            { key: "device_fingerprint", header: "Device", render: (r) => r.device_fingerprint.slice(0, 12) + "…" },
            { key: "login_time", header: "Signed In", render: (r) => new Date(r.login_time).toLocaleString() },
            { key: "session_expiry", header: "Expires", render: (r) => new Date(r.session_expiry).toLocaleString() },
            { key: "actions", header: "", render: (r) => <Button size="sm" variant="danger" onClick={() => revokeSession(r.session_id)}>Revoke</Button> },
          ]}
        />
      </Card>

      <Card title="Backup Codes (MFA)" className="section-spacing">
        <p>MFA itself is enforced by Azure AD B2C on new devices or after 30+ days inactive. Backup codes let you sign in if you lose access to your usual MFA method.</p>
        <Button onClick={generateBackupCodes} disabled={busy}>Generate New Backup Codes</Button>
      </Card>

      <Card title="Security Questions (Optional)" className="section-spacing">
        {answers.map((a, i) => (
          <input id="security-center-tab-a" name="a"
            key={i}
            placeholder={`Answer ${i + 1}`}
            value={a}
            onChange={(e) => setAnswers((prev) => prev.map((v, idx) => (idx === i ? e.target.value : v)))}
            className="touch-target"
            style={{ width: "100%", marginBottom: "var(--space-2)" }}
          />
        ))}
        <Button onClick={saveSecurityQuestions} disabled={busy}>Save</Button>
      </Card>

      <Modal open={!!backupCodes} onClose={() => setBackupCodes(null)} title="Your Backup Codes">
        <p>Save these somewhere safe — each code works once, and they won't be shown again.</p>
        <ul>
          {backupCodes?.map((c) => <li key={c} style={{ fontFamily: "monospace", fontSize: "1.1rem" }}>{c}</li>)}
        </ul>
      </Modal>

      <ConfirmDialog
        open={confirmLogoutAll}
        onClose={() => setConfirmLogoutAll(false)}
        onConfirm={logoutAll}
        title="Log out all other sessions?"
        confirmLabel="Log Out All"
        danger
        busy={busy}
      >
        <p>This device stays signed in; every other device will need to sign in again.</p>
      </ConfirmDialog>
    </div>
  );
}
